import { useEffect, useRef, useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { toast } from "sonner"
import useDefaultLanding from "./useDefaultLanding"
import LoadingScreen from "./LoadingScreen"
import { parseUnknownError, useLocale } from "@/base"
import { useApplyLoginLinkMutation, useRedirectAfterLogin } from "@/auth"
import { useT } from "@/i18n"

// Invitation links carry their post-login destination (e.g. the
// questionnaire at /r/{id}) in a `redirect` param appended outside the
// signed hash — only follow internal paths so a tampered link can't turn
// the sign-in into an open redirect. Returns null when there is no usable
// redirect, so the caller falls back to the default landing.
const safeInternalRedirect = (path?: string): string | null =>
  path && /^\/(?!\/)/.test(path) && !path.includes("\\") ? path : null

type Props = {
  params: Record<string, string>
}

// Route has to match the rewritten link URL, see backend/src/Security/LoginLinkSender.php
export default function ApplyLoginLink({ params }: Props) {
  const { t } = useT("auth")
  const [applyLoginLink] = useApplyLoginLinkMutation()
  const { getRedirectUrl, peekRedirectUrl } = useRedirectAfterLogin()
  const navigate = useNavigate()
  const locale = useLocale()

  const explicit = safeInternalRedirect(params.redirect)
  const [signedIn, setSignedIn] = useState(false)
  const appliedRef = useRef(false)

  // Login links are single-use server-side, so consume the link exactly once
  // even though React (Strict Mode) may invoke this effect twice.
  useEffect(() => {
    if (appliedRef.current) {
      return
    }
    appliedRef.current = true

    void (async () => {
      try {
        await applyLoginLink(params).unwrap()
        toast.success(t("signedIn"))
        setSignedIn(true)
      } catch (e) {
        const violations = parseUnknownError(e)
        const fallback = t("loginLinkError")

        toast.error(violations ? (violations[0]?.title ?? fallback) : fallback)
        await navigate({
          to: "/$locale/auth",
          params: { locale },
          replace: true,
        })
      }
    })()
  }, [params, applyLoginLink, navigate, t, locale])

  // Post-sign-in precedence mirrors AuthRoute (which opts out of /auth/link so
  // this is the sole navigator here):
  //   1. an invitation's explicit redirect to the questionnaire,
  //   2. a deep-link the User was bounced from before requesting the link,
  //   3. the computed default landing (/myprofiles for an active assignment).
  // Only fetch the default when neither explicit nor stored redirect applies.
  const hasStoredRedirect = signedIn && !explicit && !!peekRedirectUrl()
  const { route, isResolved } = useDefaultLanding(
    signedIn && !explicit && !hasStoredRedirect,
  )

  useEffect(() => {
    if (!signedIn) {
      return
    }
    if (explicit) {
      void navigate({ to: explicit, replace: true })

      return
    }
    // Wait for the default to resolve, unless a stored redirect makes it moot.
    if (!hasStoredRedirect && !isResolved) {
      return
    }
    // getRedirectUrl returns (and consumes) the stored redirect when present,
    // falling back to the computed default otherwise.
    void navigate({ to: getRedirectUrl(route), replace: true })
  }, [
    signedIn,
    explicit,
    hasStoredRedirect,
    isResolved,
    route,
    navigate,
    getRedirectUrl,
  ])

  return <LoadingScreen />
}
